

export const dynamic = 'force-dynamic';

import { useState, useEffect } from "react";
import VendorReportsLayout from "../VendorReportsLayout";
import { reportsApi } from "@/lib/api";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Commission {
  id: number;
  vendor_name: string;
  vendor_type?: string;
  event_name: string;
  client_name?: string;
  event_date: string;
  description?: string;
  commission_type: string;
  commission_percent: number;
  commission_amount: number;
  total_amount: number;
  status: string;
  created_at: string;
}

interface CommissionSummary {
  total_commission: number;
  total_assignments: number;
  average_commission: number;
  by_vendor: Array<{ id: number; name: string; total_commission: number; assignment_count: number }>;
  by_commission_type: Array<{ commission_type: string; total_commission: number; count: number }>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n ?? 0);

const fmtDate = (d: string) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ─── Page Component ───────────────────────────────────────────────────────────

export default function VendorCommissionsPage() {
  const [period, setPeriod] = useState<{ from: string; to: string }>({ from: "", to: "" });
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [summary, setSummary] = useState<CommissionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    const params: any = { page: String(page), per_page: "20" };
    if (period.from && period.to) {
      params.from_date = period.from;
      params.to_date = period.to;
    }

    reportsApi
      .vendorCommissions(params)
      .then((res: any) => {
        if (res?.success) {
          const data = res.data.commissions;
          setCommissions(data.data || data || []);
          setSummary(res.data.summary || null);
          setLastPage(data.last_page ?? 1);
        }
      })
      .catch((err) => console.error("Failed to load vendor commissions:", err))
      .finally(() => setLoading(false));
  }, [period, page]);

  return (
    <VendorReportsLayout onPeriodChange={setPeriod}>
      {/* ── Summary Cards ── */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-violet-700">{fmt(summary.total_commission)}</div>
            <div className="text-xs text-stone-400">Total Commission</div>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-stone-800">{summary.total_assignments}</div>
            <div className="text-xs text-stone-400">Assignments</div>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-amber-700">{fmt(summary.average_commission)}</div>
            <div className="text-xs text-stone-400">Avg Commission</div>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-emerald-700">{summary.by_vendor.length}</div>
            <div className="text-xs text-stone-400">Vendors</div>
          </div>
        </div>
      )}

      {/* ── Commission Breakdown by Vendor ── */}
      {summary && summary.by_vendor.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-200 p-5">
          <h3 className="font-bold text-stone-800 mb-4 font-serif text-sm">Commission by Vendor</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {summary.by_vendor.slice(0, 6).map((vendor) => (
              <div key={vendor.id} className="bg-stone-50 rounded-xl p-4 border border-stone-200">
                <div className="font-semibold text-stone-800 text-sm mb-1">{vendor.name}</div>
                <div className="text-lg font-bold text-violet-700">{fmt(vendor.total_commission)}</div>
                <div className="text-xs text-stone-400">{vendor.assignment_count} assignments</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Commission Details Table ── */}
      <div className="bg-white rounded-2xl border border-stone-200">
        <div className="px-5 py-4 border-b border-stone-100">
          <h3 className="font-bold text-stone-800 font-serif text-sm">Commission Details</h3>
        </div>

        {loading ? (
          <div className="p-5 space-y-3 animate-pulse">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-stone-50 rounded-xl" />
            ))}
          </div>
        ) : commissions.length === 0 ? (
          <div className="p-8 text-center text-sm text-stone-400 italic">No commission data found for this period.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase">Vendor</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Event</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Date</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Type</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase">%</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Commission</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-50">
                  {commissions.map((commission) => (
                    <tr key={commission.id} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="font-semibold text-stone-800">{commission.vendor_name}</div>
                        {commission.vendor_type && <div className="text-xs text-stone-400">{commission.vendor_type}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-stone-700">{commission.event_name}</div>
                        {commission.client_name && <div className="text-xs text-stone-400">{commission.client_name}</div>}
                      </td>
                      <td className="px-4 py-3 text-stone-500 text-xs">{fmtDate(commission.event_date)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-700">
                          {commission.commission_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-stone-600">
                        {commission.commission_type === 'percentage' ? `${commission.commission_percent}%` : 'Fixed'}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-violet-700">{fmt(commission.commission_amount)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          commission.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          commission.status === 'completed' ? 'bg-violet-100 text-violet-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {commission.status?.replace(/_/g, " ")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {lastPage > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-stone-100">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-xs rounded-lg border border-stone-200 disabled:opacity-40 hover:border-stone-300 transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-stone-500">Page {page} of {lastPage}</span>
                <button
                  onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                  disabled={page === lastPage}
                  className="px-3 py-1.5 text-xs rounded-lg border border-stone-200 disabled:opacity-40 hover:border-stone-300 transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </VendorReportsLayout>
  );
}
